import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Info, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { isApiError, type Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { useNetworkRequests } from '../../api/queries';
import type {
  ChangedRequest,
  Network,
  RequestDecision,
  RequestDecisionKind,
  RequestGroup,
  RequestInstance,
  RequestListFilter,
  RequestPartName,
} from '../../api/types';
import { useAuth, usePermission } from '../../auth/context';
import { useToast } from '../../components/toast';
import { ActionButton, Button, Dialog, ErrorMessage, Loading } from '../../components/ui';
import { useCardTable } from '../../lib/cardTable';
import { formatDay, formatRelative } from '../../lib/format';
import { strings } from '../../strings';
import { useAllAttachments } from '../page/hooks';
import { useGeneratedFlows } from './flows';
import { DecisionBadge, RequestStateBadge } from './parts';
import { decisionPart } from './tones';
import { TrafficFlows } from './TrafficFlows';

const r = strings.networks.requests;

interface Row {
  group: RequestGroup;
  instance: RequestInstance;
  key: string;
}

const rowKey = (g: RequestGroup, i: Pick<RequestInstance, 'sourceDeploymentId' | 'provider' | 'region'>) =>
  `${i.sourceDeploymentId}/${g.requestId}/${i.provider}/${i.region}`;
const changedKey = (c: ChangedRequest) => `${c.sourceDeploymentId}/${c.requestId}/${c.provider}/${c.region}`;
const instanceKey = (row: Row) => ({
  sourceDeploymentId: row.instance.sourceDeploymentId,
  requestId: row.group.requestId,
  provider: row.instance.provider,
  region: row.instance.region,
});
const rowName = (row: Row) =>
  r.instanceName(
    row.instance.sourceDeploymentName,
    row.instance.targetDeploymentName ?? r.noTarget,
    row.instance.region,
  );

function filterFrom(search: URLSearchParams): RequestListFilter {
  const list = <T extends string>(key: string) =>
    (search.get(key)?.split(',').filter(Boolean) as T[] | undefined) ?? undefined;
  return {
    decision: list<RequestDecision>('decision'),
    enforcement: list<RequestInstance['enforcement']>('enforcement'),
    sourceBlueprintId: search.get('source'),
    targetBlueprintId: search.get('target'),
    neededForMicroSegmentation: search.get('needed') === '1',
  };
}

type DialogState = { kind: RequestDecisionKind | 'ask-again'; rows: Row[]; bulk: boolean } | null;

/**
 * The network's Traffic tab (spec 11 6.15.21, mock n09): what the topology does with traffic, the requests grouped by
 * blueprint request with decision and enforcement per instance, filters, bulk decisions with one summary dialog, the
 * effect of every decision before it is made, the edge cases of U3.3, and the generated flows and egress.
 */
export function TrafficTab({ network: versioned }: { network: Versioned<Network> }) {
  const n = versioned.data;
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me, config } = useAuth();
  const manage = usePermission('manage-networks');
  const design = usePermission('design');
  const operate = usePermission('operate');
  const canAsk = design || operate;
  const ids = useId();
  const cardTable = useCardTable();
  const [search, setSearch] = useSearchParams();
  const filter = useMemo(() => filterFrom(search), [search]);
  const all = useNetworkRequests(n.id);
  const needed = useNetworkRequests(
    n.id,
    { neededForMicroSegmentation: true },
    {
      enabled: n.settings.topology.kind !== 'micro-segmentation',
    },
  );
  const filtered = useNetworkRequests(n.id, filter);
  const attachments = useAllAttachments(n.id);
  const flows = useGeneratedFlows(attachments.data);
  const pair = search.get('pair')?.split('~') ?? null;
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<DialogState>(null);
  const [busy, setBusy] = useState(false);
  const [sameApproverOn, setSameApproverOn] = useState(!!config.networking?.requireDifferentApprover);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [changed, setChanged] = useState<{ names: string[]; others: Row[] } | null>(null);

  const kind = n.settings.topology.kind;
  const enforcing = kind === 'micro-segmentation';
  const groups = filtered.data ?? [];
  const rows: Row[] = groups.flatMap((g) =>
    g.instances
      .filter(
        (i) =>
          !pair ||
          (pair.includes(i.sourceDeploymentId) &&
            !!i.targetDeploymentId &&
            pair.includes(i.targetDeploymentId)),
      )
      .map((i) => ({ group: g, instance: i, key: rowKey(g, i) })),
  );
  const shownGroups = groups.filter((g) => rows.some((x) => x.group === g));
  const allInstances = (all.data ?? []).flatMap((g) => g.instances);
  const pendingCount = allInstances.filter(
    (i) => i.applied?.decision === 'pending' || i.proposed?.decision === 'pending',
  ).length;
  const approvedCount = allInstances.filter(
    (i) => i.applied?.decision === 'approved' && !i.withdrawnAt,
  ).length;
  const neededCount = (needed.data ?? []).reduce((sum, g) => sum + g.instances.length, 0);
  const groupNames = (side: 'source' | 'target') => {
    const map = new Map<string, string>();
    for (const g of all.data ?? [])
      if (side === 'source') map.set(g.sourceBlueprint.id, g.sourceBlueprint.name);
      else map.set(g.target.blueprintId, g.target.blueprintName);
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  };
  // Rule groups per region past 80 % (U4.2).
  const budgets = new Map<string, { used: number; limit: number }>();
  for (const i of allInstances)
    if (i.firewall) budgets.set(i.firewall.region, { used: i.firewall.used, limit: i.firewall.limit });
  const crowded = [...budgets.entries()].filter(([, b]) => b.limit > 0 && b.used / b.limit > 0.8);

  const setParam = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    next.set('tab', 'traffic');
    setSearch(next, { replace: true });
  };
  const quick =
    search.get('needed') === '1' ? 'needed' : search.get('decision') === 'pending' ? 'pending' : 'all';

  const reload = () => queryClient.invalidateQueries({ queryKey: ['network', n.id] });

  // ---- what each control may do, with its reason when it may not (U3.3, U4.2)
  const limitReason = (i: RequestInstance) =>
    enforcing && i.firewall && i.firewall.used >= i.firewall.limit
      ? r.groupLimit(n.name, i.firewall.region, i.firewall.limit)
      : null;
  const sameApprover = (i: RequestInstance, part: RequestPartName | null) => {
    const p = part === 'proposed' ? i.proposed : i.applied;
    return sameApproverOn && !!p?.introducedBy?.by && p.introducedBy.by === me.name ? r.sameApprover : null;
  };
  const approveReason = (i: RequestInstance) => {
    const part = decisionPart(i, 'approve');
    return sameApprover(i, part) ?? (part === 'applied' ? limitReason(i) : null);
  };

  const finish = async (message: string) => {
    setDialog(null);
    setSelected(new Set());
    setReason('');
    setReasonError(false);
    setDialogError(null);
    await reload();
    toast.show({ tone: 'success', title: message });
  };

  const failed = async (error: unknown, rowsOf: Row[]) => {
    if (isApiError(error, 'DECISION_BY_REQUESTER')) {
      setSameApproverOn(true);
      setDialogError(r.sameApprover);
    } else if (isApiError(error, 'PRECONDITION_FAILED')) {
      const list = ((error.problem as { changed?: ChangedRequest[] }).changed ??
        (error.problem as { extensions?: { changed?: ChangedRequest[] } }).extensions?.changed ??
        []) as ChangedRequest[];
      const keys = new Set(list.map(changedKey));
      const names = rowsOf
        .filter((x) => keys.has(x.key) || list.length === 0)
        .map((x) =>
          r.changedMeanwhile(rowName(x), list.find((c) => changedKey(c) === x.key)?.decidedBy ?? null),
        );
      await reload();
      setChanged({ names, others: rowsOf.filter((x) => !keys.has(x.key) && list.length > 0) });
      setDialogError(null);
    } else if (isApiError(error, 'REQUEST_DECISION_INVALID')) {
      await reload();
      setDialogError(r.requestChanged);
    } else if (isApiError(error, 'FIREWALL_RULE_GROUPS_LIMIT')) {
      const p = error.problem as { used?: number; limit?: number; region?: string };
      setDialogError(r.groupLimit(n.name, p.region ?? '', p.limit ?? 0));
      await reload();
    } else if (isApiError(error, 'REASON_REQUIRED')) setReasonError(true);
    else setDialogError(isApiError(error) ? (error.detail ?? error.message) : String(error));
  };

  const decideOne = async (row: Row, kind: RequestDecisionKind, text: string | null) => {
    const part = decisionPart(row.instance, kind);
    if (!part) return;
    setBusy(true);
    try {
      const result = await api.decideNetworkRequest(
        n.id,
        instanceKey(row),
        kind,
        { part, ...(text ? { reason: text } : {}) },
        row.instance.version,
      );
      const queued = result.enforcement === 'opening' || result.enforcement === 'closing';
      await finish(
        !enforcing && kind === 'approve'
          ? r.recordedToast(n.name)
          : `${r.decidedToast(r.decision[kind === 'restore' ? 'approved' : kind === 'approve' ? 'approved' : kind === 'reject' ? 'rejected' : 'revoked'].toLowerCase(), 1)}${queued ? ` ${r.queuedRun}` : ''}`,
      );
    } catch (error) {
      await failed(error, [row]);
    } finally {
      setBusy(false);
    }
  };

  const decideMany = async (list: Row[], kind: 'approve' | 'reject', text: string | null) => {
    setBusy(true);
    try {
      const byPart = new Map<RequestPartName, Row[]>();
      for (const row of list) {
        const part = decisionPart(row.instance, kind);
        if (!part) continue;
        byPart.set(part, [...(byPart.get(part) ?? []), row]);
      }
      let count = 0;
      for (const [part, partRows] of byPart) {
        const result = await api.decideNetworkRequests(n.id, {
          decision: kind,
          part,
          ...(text ? { reason: text } : {}),
          instances: partRows.map((row) => ({ ...instanceKey(row), version: row.instance.version })),
        });
        count += result.instances.length;
      }
      await finish(
        !enforcing && kind === 'approve'
          ? r.recordedToast(n.name)
          : r.decidedToast(kind === 'approve' ? 'approved' : 'rejected', count),
      );
    } catch (error) {
      await failed(error, list);
    } finally {
      setBusy(false);
    }
  };

  const askAgain = async (row: Row, text: string) => {
    const part: RequestPartName =
      row.instance.proposed && ['rejected', 'revoked'].includes(row.instance.proposed.decision)
        ? 'proposed'
        : 'applied';
    setBusy(true);
    try {
      await api.askAgainNetworkRequest(n.id, instanceKey(row), { part, reason: text }, row.instance.version);
      await finish(r.askedAgainLine(formatDay(new Date().toISOString()), me.name));
    } catch (error) {
      await failed(error, [row]);
    } finally {
      setBusy(false);
    }
  };

  const retry = async (row: Row) => {
    try {
      const run = await api.enforceNetworkRequest(n.id, instanceKey(row), row.instance.version);
      await reload();
      toast.show({ tone: 'success', title: r.queuedRun, detail: run.id });
    } catch (error) {
      toast.error(error);
      await reload();
    }
  };

  const open = (kind: RequestDecisionKind | 'ask-again', list: Row[], bulk = false) => {
    setReason('');
    setReasonError(false);
    setDialogError(null);
    setChanged(null);
    setDialog({ kind, rows: list, bulk });
  };

  const approve = (row: Row) => {
    if (
      !enforcing &&
      decisionPart(row.instance, 'approve') !== null &&
      row.instance.applied?.decision !== 'rejected' &&
      row.instance.proposed?.decision !== 'rejected'
    )
      void decideOne(row, 'approve', null);
    else open('approve', [row]);
  };

  const confirm = () => {
    if (!dialog) return;
    const needsReason =
      dialog.kind !== 'approve' ||
      dialog.rows.some(
        (x) =>
          x.instance.applied?.decision === 'rejected' && decisionPart(x.instance, 'approve') === 'applied',
      );
    const text = reason.trim();
    if (needsReason && dialog.kind !== 'approve' && !text) {
      setReasonError(true);
      return;
    }
    if (dialog.kind === 'ask-again') void askAgain(dialog.rows[0], text);
    else if (dialog.bulk) void decideMany(dialog.rows, dialog.kind as 'approve' | 'reject', text || null);
    else void decideOne(dialog.rows[0], dialog.kind, text || null);
  };

  const selectable = (row: Row) =>
    manage &&
    (decisionPart(row.instance, 'approve') !== null || decisionPart(row.instance, 'reject') !== null);
  const selectedRows = rows.filter((x) => selected.has(x.key));
  const toggle = (key: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  // ---- the room left per region for the approvals in a dialog
  const room = (list: Row[]) => {
    if (!enforcing) return [];
    const need = new Map<string, number>();
    for (const x of list)
      if (x.instance.firewall && decisionPart(x.instance, 'approve') === 'applied')
        need.set(x.instance.firewall.region, (need.get(x.instance.firewall.region) ?? 0) + 1);
    return [...need.entries()].map(([region, count]) => {
      const b = budgets.get(region)!;
      return { region, count, room: Math.max(0, b.limit - b.used) };
    });
  };

  if (filtered.error && !filtered.data) return <ErrorMessage error={filtered.error} />;

  const intro =
    kind === 'micro-segmentation'
      ? r.introMicro(flows?.length ?? 0, approvedCount)
      : kind === 'mesh' || (kind === 'hub-spoke' && n.settings.topology.spokeToSpoke === 'via-hub')
        ? r.introOpen(kind)
        : r.introClosed;

  const actionsOf = (row: Row): ReactNode => {
    const i = row.instance;
    if (i.withdrawnAt) return null;
    const buttons: ReactNode[] = [];
    const rejectedOrRevoked =
      i.applied?.decision === 'rejected' ||
      i.applied?.decision === 'revoked' ||
      i.proposed?.decision === 'rejected';
    if (manage) {
      if (decisionPart(i, 'approve') !== null) {
        const rejected =
          (decisionPart(i, 'approve') === 'applied' ? i.applied : i.proposed)?.decision === 'rejected';
        buttons.push(
          <ActionButton
            key="approve"
            permission="manage-networks"
            className="btn-sm"
            variant={rejected ? undefined : 'primary'}
            disabledReason={approveReason(i)}
            icon={<Check aria-hidden className="size-3.5" />}
            aria-label={`${rejected ? r.approveEllipsis : r.approve} ${rowName(row)}`}
            onClick={() => (rejected ? open('approve', [row]) : approve(row))}
          >
            {rejected ? r.approveEllipsis : r.approve}
          </ActionButton>,
        );
      }
      if (decisionPart(i, 'reject') !== null)
        buttons.push(
          <Button
            key="reject"
            className="btn-sm"
            aria-label={`${r.reject} ${rowName(row)}`}
            onClick={() => open('reject', [row])}
          >
            {r.reject}
          </Button>,
        );
      if (decisionPart(i, 'revoke') !== null)
        buttons.push(
          <Button
            key="revoke"
            className="btn-sm"
            aria-label={`${r.revoke} ${rowName(row)}`}
            onClick={() => open('revoke', [row])}
          >
            {r.revoke}
          </Button>,
        );
      if (decisionPart(i, 'restore') !== null)
        buttons.push(
          <ActionButton
            key="restore"
            permission="manage-networks"
            className="btn-sm"
            disabledReason={sameApprover(i, 'applied') ?? limitReason(i)}
            aria-label={`${r.restore} ${rowName(row)}`}
            onClick={() => open('restore', [row])}
          >
            {r.restore}
          </ActionButton>,
        );
    } else if (canAsk && rejectedOrRevoked) {
      buttons.push(
        <ActionButton
          key="ask"
          permission="read"
          className="btn-sm"
          disabledReason={i.askedAgain ? r.askedAgainLine(formatDay(i.askedAgain.at), i.askedAgain.by) : null}
          aria-label={`${r.askAgain} ${rowName(row)}`}
          onClick={() => open('ask-again', [row])}
        >
          {r.askAgain}
        </ActionButton>,
      );
    }
    return buttons.length ? <span className="flex flex-wrap gap-1.5">{buttons}</span> : null;
  };

  const enforcementOf = (row: Row) => {
    const i = row.instance;
    const decision = i.applied?.decision ?? i.proposed?.decision ?? null;
    return (
      <span className="flex flex-col items-start gap-1">
        <RequestStateBadge
          state={i.state}
          enforcement={i.enforcement}
          enforcing={enforcing}
          decision={decision}
        />
        {(i.enforcement === 'opening' || i.enforcement === 'closing') && i.enforcementRunId && (
          <Link className="link text-[12px]" to={`/runs/${i.enforcementRunId}`}>
            {r.open}
          </Link>
        )}
        {i.enforcement === 'failed' &&
          (manage ? (
            <Button className="btn-sm" onClick={() => void retry(row)}>
              <RefreshCw aria-hidden className="size-3.5" />
              {r.retry}
            </Button>
          ) : (
            <span className="text-[12px]">{r.askAdminRetry(n.name)}</span>
          ))}
      </span>
    );
  };

  const decisionOf = (row: Row) => {
    const i = row.instance;
    return (
      <span className="flex flex-col items-start gap-1">
        {i.applied && <DecisionBadge decision={i.applied.decision} />}
        {i.proposed && (
          <span className="text-[12px] text-fg-muted">
            {r.proposedBy(
              row.group.sourceBlueprint.name,
              i.proposed.introducedBy?.version ?? 0,
              i.proposed.shape.targetResourceId,
              i.proposed.shape.port,
            )}
          </span>
        )}
        {i.proposed && <DecisionBadge decision={i.proposed.decision} />}
      </span>
    );
  };

  const detailsOf = (row: Row) => {
    const i = row.instance;
    const shape = i.applied ?? i.proposed;
    return (
      <span className="block text-[12px] text-fg-muted">
        {shape?.introducedBy && (
          <span className="block">
            {r.shapeBy(row.group.sourceBlueprint.name, shape.introducedBy.version, shape.introducedBy.by)}
          </span>
        )}
        {i.decidedBy && i.decidedAt && (
          <span className="block">
            {r.decidedBy(i.decidedBy, formatRelative(i.decidedAt), i.decisionReason)}
          </span>
        )}
        {i.askedAgain && (
          <span className="block font-medium text-fg" data-testid="asked-again">
            {r.askedAgainLine(formatDay(i.askedAgain.at), i.askedAgain.by)} ·{' '}
            {r.askedAgainReason(i.askedAgain.reason)}
          </span>
        )}
      </span>
    );
  };

  const dialogRows = dialog?.rows ?? [];
  const dialogRoom = dialog && dialog.kind === 'approve' ? room(dialogRows) : [];
  const overRoom = dialogRoom.some((x) => x.count > x.room);
  const effectOf = (row: Row, kind: RequestDecisionKind | 'ask-again') =>
    kind === 'ask-again' ? null : row.instance.effects[kind];

  return (
    <div className="space-y-4" data-testid="traffic-tab">
      <p className={`callout ${enforcing ? 'callout-info' : 'callout-info'}`} data-testid="traffic-intro">
        {enforcing ? (
          <ShieldCheck aria-hidden className="size-4 shrink-0" />
        ) : (
          <Info aria-hidden className="size-4 shrink-0" />
        )}
        <span>{intro}</span>
      </p>
      {crowded.map(([region, b]) => (
        <p key={region} className="callout callout-warning" data-testid="rule-groups">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span>{r.groupCount(b.used, b.limit, region)}</span>
        </p>
      ))}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-[15px] font-semibold">{r.requests}</h2>
        <div role="group" aria-label={r.filters} className="segmented">
          <button
            type="button"
            className="segmented-item"
            aria-pressed={quick === 'pending'}
            onClick={() => setParam({ decision: 'pending', needed: null })}
          >
            {r.needsDecision(pendingCount)}
          </button>
          <button
            type="button"
            className="segmented-item"
            aria-pressed={quick === 'all'}
            onClick={() => setParam({ decision: null, needed: null })}
          >
            {r.all}
          </button>
          {!enforcing && (
            <button
              type="button"
              className="segmented-item"
              aria-pressed={quick === 'needed'}
              onClick={() => setParam({ decision: null, needed: '1' })}
            >
              {r.neededForMicro(neededCount)}
            </button>
          )}
        </div>
        {manage && (
          <span className="ml-auto flex flex-wrap gap-2">
            <ActionButton
              permission="manage-networks"
              disabledReason={selectedRows.length ? null : r.selectFirst}
              icon={<Check aria-hidden className="size-4" />}
              onClick={() => open('approve', selectedRows, true)}
            >
              {r.approveSelected(selectedRows.length)}
            </ActionButton>
            <ActionButton
              permission="manage-networks"
              disabledReason={
                selectedRows.some((x) => decisionPart(x.instance, 'reject')) ? null : r.selectFirst
              }
              onClick={() =>
                open(
                  'reject',
                  selectedRows.filter((x) => decisionPart(x.instance, 'reject')),
                  true,
                )
              }
            >
              {r.rejectSelected}
            </ActionButton>
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        {(
          [
            [
              'decision',
              r.decisionFilter,
              ['pending', 'approved', 'rejected', 'revoked'].map((d) => [d, r.decision[d]]),
            ],
            [
              'enforcement',
              r.enforcementFilter,
              ['not-enforced', 'opening', 'open', 'closing', 'closed', 'failed'].map((e) => [e, e]),
            ],
            ['source', r.sourceFilter, groupNames('source')],
            ['target', r.targetFilter, groupNames('target')],
          ] as [string, string, [string, string][]][]
        ).map(([key, label, options]) => (
          <label key={key} className="flex flex-col gap-1">
            <span className="label">{label}</span>
            <select
              className="input"
              value={search.get(key) ?? ''}
              onChange={(e) =>
                setParam({ [key]: e.target.value || null, ...(key === 'decision' ? { needed: null } : {}) })
              }
            >
              <option value="">{r.any}</option>
              {options.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </label>
        ))}
        {pair && (
          <span className="flex items-center gap-2 pb-1">
            <span className="badge tone-info">
              {r.pairFilter(...(nameOfPair(pair, all.data) as [string, string]))}
            </span>
            <button type="button" className="link" onClick={() => setParam({ pair: null })}>
              {r.clearPair}
            </button>
          </span>
        )}
      </div>

      {!filtered.data ? (
        <Loading />
      ) : shownGroups.length === 0 ? (
        <p className="text-sm text-fg-muted">{(all.data ?? []).length ? r.noMatch : r.noRequests}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table
            ref={cardTable}
            className="table table-cards net-traffic-table"
            aria-label={r.tableLabel(n.name)}
          >
            <thead>
              <tr>
                {manage && (
                  <th scope="col" className="w-8">
                    <span className="sr-only">{r.columns.select}</span>
                  </th>
                )}
                <th scope="col">{r.columns.sourceTarget}</th>
                <th scope="col">{r.columns.decision}</th>
                <th scope="col">{r.columns.enforcement}</th>
                <th scope="col">{r.columns.details}</th>
                <th scope="col">
                  <span className="sr-only">{r.columns.actions}</span>
                </th>
              </tr>
            </thead>
            {shownGroups.map((g) => {
              const gid = `${g.sourceBlueprint.id}/${g.requestId}`;
              const groupRows = rows.filter((x) => x.group === g);
              const pendingRows = groupRows.filter(
                (x) =>
                  decisionPart(x.instance, 'approve') !== null &&
                  x.instance.applied?.decision !== 'rejected' &&
                  x.instance.proposed?.decision !== 'rejected' &&
                  !approveReason(x.instance),
              );
              const isOpen = !collapsed.has(gid);
              const port = (g.instances[0]?.applied ?? g.instances[0]?.proposed)?.shape.port;
              const name = r.groupName(
                g.sourceBlueprint.name,
                g.requestId,
                g.target.blueprintName,
                g.target.resourceId,
                port ? r.tcp(port) : '',
              );
              return (
                <tbody key={gid} data-testid={`request-group-${g.requestId}`}>
                  <tr className="net-traffic-group">
                    <td colSpan={manage ? 5 : 4}>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 text-left"
                        aria-expanded={isOpen}
                        aria-controls={`${ids}-${gid}`}
                        onClick={() =>
                          setCollapsed((c) => {
                            const next = new Set(c);
                            if (next.has(gid)) next.delete(gid);
                            else next.add(gid);
                            return next;
                          })
                        }
                      >
                        {isOpen ? (
                          <ChevronDown aria-hidden className="size-4 shrink-0" />
                        ) : (
                          <ChevronRight aria-hidden className="size-4 shrink-0" />
                        )}
                        <span className="font-semibold">{name}</span>
                        <span className="text-[12.5px] text-fg-muted">“{g.reason}”</span>
                      </button>
                    </td>
                    <td className="text-right">
                      {manage && pendingRows.length > 0 && (
                        <Button
                          className="btn-sm"
                          onClick={() =>
                            enforcing || pendingRows.length > 1
                              ? open('approve', pendingRows, true)
                              : void decideMany(pendingRows, 'approve', null)
                          }
                        >
                          {r.approveAllPending(pendingRows.length)}
                        </Button>
                      )}
                    </td>
                  </tr>
                  {isOpen &&
                    groupRows.map((row) => (
                      <tr key={row.key} id={`${ids}-${gid}`} data-testid="request-row">
                        {manage && (
                          <td>
                            {selectable(row) && (
                              <input
                                type="checkbox"
                                aria-label={r.selectInstance(rowName(row))}
                                checked={selected.has(row.key)}
                                onChange={(e) => toggle(row.key, e.target.checked)}
                              />
                            )}
                          </td>
                        )}
                        <th scope="row" className="font-normal">
                          <span className="font-mono text-[12.5px] font-semibold">
                            {row.instance.sourceDeploymentName} →{' '}
                            {row.instance.targetDeploymentName ?? r.noTarget}
                          </span>
                          <span className="block font-mono text-[12px] text-fg-muted">
                            {row.instance.region}
                          </span>
                        </th>
                        <td>{decisionOf(row)}</td>
                        <td>{enforcementOf(row)}</td>
                        <td>{detailsOf(row)}</td>
                        <td>{actionsOf(row)}</td>
                      </tr>
                    ))}
                </tbody>
              );
            })}
          </table>
        </div>
      )}

      <TrafficFlows network={n} flows={flows} />

      {changed && (
        <Dialog
          title={r.reloaded}
          onClose={() => setChanged(null)}
          footer={
            <>
              <Button onClick={() => setChanged(null)}>{strings.common.close}</Button>
              {changed.others.length > 0 && (
                <Button
                  variant="primary"
                  onClick={() => {
                    const keys = new Set(changed.others.map((x) => x.key));
                    const fresh = rows.filter((x) => keys.has(x.key));
                    setChanged(null);
                    open(dialog?.kind ?? 'approve', fresh, true);
                  }}
                >
                  {r.decideOthers(changed.others.length)}
                </Button>
              )}
            </>
          }
        >
          <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="changed-instances">
            {changed.names.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </Dialog>
      )}

      {dialog && !changed && (
        <Dialog
          title={
            dialog.kind === 'ask-again'
              ? r.askAgainTitle(dialog.rows[0].group.requestId)
              : dialog.kind === 'revoke'
                ? r.revokeTitle(rowName(dialog.rows[0]))
                : dialog.kind === 'restore'
                  ? r.restoreTitle(rowName(dialog.rows[0]))
                  : dialog.kind === 'reject'
                    ? r.rejectTitle(dialog.rows.length)
                    : r.approveTitle(dialog.rows.length)
          }
          wide={dialog.rows.length > 1}
          onClose={() => setDialog(null)}
          footer={
            <>
              <Button onClick={() => setDialog(null)}>{strings.common.cancel}</Button>
              <Button
                variant={dialog.kind === 'revoke' || dialog.kind === 'reject' ? 'danger' : 'primary'}
                disabled={busy}
                aria-disabled={overRoom || undefined}
                onClick={() => !overRoom && confirm()}
              >
                {dialog.kind === 'approve'
                  ? r.approveConfirm(dialog.rows.length)
                  : dialog.kind === 'reject'
                    ? r.confirmReject
                    : dialog.kind === 'revoke'
                      ? r.confirmRevoke
                      : dialog.kind === 'restore'
                        ? r.confirmRestore
                        : r.confirmAskAgain}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm" data-testid="decision-dialog">
            {dialog.kind === 'ask-again' && <p>{r.askAgainHelp(n.name)}</p>}
            {dialog.kind !== 'ask-again' && (
              <ul className="space-y-2" data-testid="decision-effects">
                {dialog.rows.map((row) => (
                  <li key={row.key}>
                    {dialog.rows.length > 1 && (
                      <span className="block font-mono text-[12.5px] font-semibold">{rowName(row)}</span>
                    )}
                    <span>{effectOf(row, dialog.kind) ?? strings.common.dash}</span>
                  </li>
                ))}
              </ul>
            )}
            {dialogRoom.map((x) => (
              <p
                key={x.region}
                className={`callout ${x.count > x.room ? 'callout-warning' : 'callout-info'}`}
                data-testid="room-left"
              >
                {x.count > x.room ? (
                  <TriangleAlert aria-hidden className="size-4 shrink-0" />
                ) : (
                  <Info aria-hidden className="size-4 shrink-0" />
                )}
                <span>{r.roomLeft(x.count, x.region, x.room)}</span>
              </p>
            ))}
            {(dialog.kind !== 'approve' ||
              dialog.rows.some(
                (x) =>
                  (decisionPart(x.instance, 'approve') === 'applied'
                    ? x.instance.applied
                    : x.instance.proposed
                  )?.decision === 'rejected',
              )) && (
              <div>
                <label htmlFor={`${ids}-reason`} className="label">
                  {r.reason}
                </label>
                <textarea
                  id={`${ids}-reason`}
                  className="input"
                  rows={2}
                  value={reason}
                  maxLength={500}
                  aria-invalid={reasonError || undefined}
                  aria-describedby={`${ids}-reason-help`}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError(false);
                  }}
                />
                <p id={`${ids}-reason-help`} className={reasonError ? 'text-[12.5px] text-danger' : 'help'}>
                  {reasonError ? r.reasonMissing : r.reasonRequired}
                </p>
              </div>
            )}
            {dialogError && (
              <p role="alert" className="callout callout-danger">
                <TriangleAlert aria-hidden className="size-4 shrink-0" />
                {dialogError}
              </p>
            )}
          </div>
        </Dialog>
      )}
    </div>
  );
}

function nameOfPair(pair: string[], groups: RequestGroup[] | undefined): string[] {
  const names = new Map<string, string>();
  for (const g of groups ?? [])
    for (const i of g.instances) {
      names.set(i.sourceDeploymentId, i.sourceDeploymentName);
      if (i.targetDeploymentId && i.targetDeploymentName)
        names.set(i.targetDeploymentId, i.targetDeploymentName);
    }
  return [names.get(pair[0]) ?? pair[0], names.get(pair[1]) ?? pair[1] ?? ''];
}
